(ns common.schema.option-filters
  (:require
   [clojure.string :as string]
   [common.intelligence.common :as intelligence-common]
   [common.roles :as roles]
   [common.schema.assets :as assets]
   [common.util :as cu]))

(defn person-display-string
  ([entity-or-primitive]
   (person-display-string entity-or-primitive :short))
  ([entity-or-primitive mode]
   (let [enriched-entity (intelligence-common/enrich-entity entity-or-primitive)
         name-str (->> [(:person/first-name enriched-entity)
                        (:person/last-name enriched-entity)
                        (when (= mode :long)
                          (:person/universal-email-address enriched-entity))]
                       (remove cu/blank-value?)
                       (string/join " "))]
     (if (empty? name-str)
       (string/trim (str (:person/universal-email-address enriched-entity)))
       name-str))))

(defn account-display-string
  [entity-or-primitive]
  (string/trim (string/join " "
                            [(:account/first-name entity-or-primitive)
                             (:account/last-name entity-or-primitive)])))

(defn filter-users-
  [{:keys [search-str-lc entity-candidate has-focus? entity-types-lookup include-disabled?] :as filter-args}]
  (let [empty-search? (or (not has-focus?)                                                          ;; if select does not have focus we should not filter based on the inputted text as it affects the default visible set on empty search
                          (not search-str-lc)
                          (empty? search-str-lc))
        display (string/trim (string/lower-case (or (account-display-string entity-candidate)
                                                    (person-display-string entity-candidate))))]
    (and (= (:entity/type entity-candidate) :entity.type/account)
         (->> entity-types-lookup
              :entity.type/role
              (some (fn [r]
                      (and (= (:db/id entity-candidate) (:role/subject r))
                           (roles/user-or-admin? (:role/role r))
                           (or empty-search?
                               (string/includes? display search-str-lc))
                           (or include-disabled?
                               (and (not (:common/disabled? entity-candidate))
                                    (not (:common/disabled? r)))))))))))

(defn users
  [{:keys [search-str-lc
           containing-entity
           entity-candidate
           has-focus?
           entity-types-lookup]
    :as   filter-args}]
  (let [result (filter-users- (merge filter-args
                                     {:include-disabled true}))]
    result))

(defn active-users
  [filter-args]
  (let [result (filter-users- (merge filter-args
                                     {:include-disabled false}))]
    result))

(defn not-self
  [{:keys [containing-entity entity-candidate] :as filter-args}]
  (not= (:db/id entity-candidate) (:db/id containing-entity)))

(defn pipeline-funds
  [{:keys [search-str-lc
           containing-entity
           entity-candidate
           has-focus?
           entity-types-lookup]
    :as   filter-args}]
  (let [empty-search? (or (not has-focus?)                                                          ;; if select does not have focus we should not filter based on the inputted text as it affects the default visible set on empty search
                          (not search-str-lc)
                          (empty? search-str-lc))
        display (->> entity-candidate
                     :fund/name
                     (cu/str-trim-lcase))
        valid-funds (->> entity-types-lookup
                         :entity.type/pipeline
                         (filter (fn [p]
                                   (= (:db/id p)
                                      (or (:oppo/pipeline containing-entity)
                                          (:fundraising-opportunity/pipeline containing-entity)
                                          (:sales-opportunity/pipeline containing-entity)
                                          (:exit-opportunity/pipeline containing-entity)))))
                         (first)
                         :pipeline/valid-funds)]
    (and (= (:entity/type entity-candidate) :entity.type/fund)
         (or empty-search?
             (string/includes? display search-str-lc))
         (or (empty? valid-funds)
             (some #{(:db/id entity-candidate)} valid-funds)))))

(defn pipelines
  [pipeline-entity-type
   {:keys [search-str-lc
           containing-entity
           entity-candidate
           has-focus?
           entity-types-lookup]
    :as   filter-args}]
  (let [text-contains? (or (empty? search-str-lc)
                           (string/includes? (cu/str-trim-lcase (:pipeline/name entity-candidate)) search-str-lc))
        res (and (= pipeline-entity-type (:pipeline/entity-type entity-candidate))
                 text-contains?)]
    res))

(defn asset-type-pipelines
  [pipeline-entity-type asset-types
   {:keys [search-str-lc
           entity-candidate
           has-focus?
           entity-types-lookup]
    :as   filter-args}]
  (let [asset-types (if (coll? asset-types)
                      asset-types
                      [asset-types])
        text-contains? (or (empty? search-str-lc)
                           (string/includes? (cu/str-trim-lcase (:pipeline/name entity-candidate)) search-str-lc))
        res (and (= pipeline-entity-type (:pipeline/entity-type entity-candidate))
                 (some (set asset-types) (:pipeline/target-asset-types entity-candidate))
                 text-contains?)]
    res))

(defn team-with-module-access
  [module
   {:keys [search-str-lc
           containing-entity
           entity-candidate
           has-focus?
           entity-types-lookup]
    :as   filter-args}]
  (let [text-contains? (or (empty? search-str-lc)
                           (string/includes? (cu/str-trim-lcase (:team/name entity-candidate)) search-str-lc))]
    (and (some #{module} (:team/modules entity-candidate))
         text-contains?)))

(defn stages-in-pipeline
  [{:keys [search-str-lc
           containing-entity
           entity-candidate
           logger
           has-focus?
           entity-types-lookup]
    :as   filter-args}]

  (let [text-contains? (or (empty? search-str-lc)
                           (string/includes? (cu/str-trim-lcase (:stage/name entity-candidate)) search-str-lc))
        ok? (or (not containing-entity)
                (when-let [pipeline (:stage/pipeline entity-candidate)]
                  (some #{pipeline}
                        [(:oppo/pipeline containing-entity)
                         (and (= (:entity/type containing-entity) :entity.type/pipeline)
                              (= (:pipeline/entity-type containing-entity)
                                 :entity.type/oppo)
                              (:db/id containing-entity))])))]
    (and ok? text-contains?)))

(defn opportunity-stages-in-pipeline
  [{:keys [search-str-lc
           containing-entity
           entity-candidate
           has-focus?
           entity-types-lookup]
    :as   filter-args}]
  (let [text-contains? (or (empty? search-str-lc)
                           (string/includes? (cu/str-trim-lcase (:opportunity-stage/name entity-candidate)) search-str-lc))
        ok? (or (not containing-entity)
                (when-let [pipeline (:opportunity-stage/pipeline entity-candidate)]
                  (some #{pipeline}
                        [(:fundraising-opportunity/pipeline containing-entity)
                         (and (= (:entity/type containing-entity) :entity.type/pipeline)
                              (= (:pipeline/entity-type containing-entity)
                                 :entity.type/fundraising-opportunity)
                              (:db/id containing-entity))])))]
    (and ok? text-contains?)))

(defn sales-opportunity-stages-in-pipeline
  [{:keys [search-str-lc
           containing-entity
           entity-candidate
           has-focus?
           entity-types-lookup]
    :as   filter-args}]
  (let [text-contains? (or (empty? search-str-lc)
                           (string/includes? (cu/str-trim-lcase (:sales-opportunity-stage/name entity-candidate)) search-str-lc))
        ok? (or (not containing-entity)
                (when-let [pipeline (:sales-opportunity-stage/pipeline entity-candidate)]
                  (some #{pipeline}
                        [(:sales-opportunity/pipeline containing-entity)
                         (and (= (:entity/type containing-entity) :entity.type/pipeline)
                              (= (:pipeline/entity-type containing-entity)
                                 :entity.type/sales-opportunity)
                              (:db/id containing-entity))])))]
    (and ok? text-contains?)))

(defn exit-opportunity-stages-in-pipeline
  [{:keys [search-str-lc
           containing-entity
           entity-candidate
           has-focus?
           entity-types-lookup]
    :as   filter-args}]
  (let [text-contains? (or (empty? search-str-lc)
                           (string/includes? (cu/str-trim-lcase (:exit-opportunity-stage/name entity-candidate)) search-str-lc))
        ok? (or (not containing-entity)
                (when-let [pipeline (:exit-opportunity-stage/pipeline entity-candidate)]
                  (some #{pipeline}
                        [(:exit-opportunity/pipeline containing-entity)
                         (and (= (:entity/type containing-entity) :entity.type/pipeline)
                              (= (:pipeline/entity-type containing-entity)
                                 :entity.type/exit-opportunity)
                              (:db/id containing-entity))])))]
    (and ok? text-contains?)))

(defn asset-types
  [get-valid-asset-types {:keys [search-str-lc containing-entity entity-candidate has-focus? entity-types-lookup has-feature-access-fn] :as filter-args}]
  (let [entity-type (or (:key entity-candidate)
                        (first entity-candidate))
        valid-asset-types (get-valid-asset-types containing-entity)
        valid? (and (some #{entity-type} valid-asset-types)
                    (or (not (fn? has-feature-access-fn))
                        (has-feature-access-fn (get assets/required-feature entity-type))))]
    valid?))

(defn contacts
  [{:keys [search-str-lc containing-entity entity-candidate has-focus? entity-types-lookup include-disabled?] :as filter-args}]
  (let [empty-search? (or (not has-focus?)                                                          ;; if select does not have focus we should not filter based on the inputted text as it affects the default visible set on empty search
                          (not search-str-lc)
                          (empty? search-str-lc))
        display (string/trim (string/lower-case (person-display-string entity-candidate)))]
    (and (= (:entity/type entity-candidate) :entity.type/person)
         (or empty-search?
             (string/includes? display search-str-lc))
         (or include-disabled?
             (not (:common/disabled? entity-candidate))))))

(defn combine-or
  [fn1 fn2]
  (fn tmp [filter-args]
    (or (fn1 filter-args)
        (fn2 filter-args))))

(defn combine-and
  [fn1 fn2]
  (fn tmp [filter-args]
    (and (fn1 filter-args)
         (fn2 filter-args))))
